import React, { useEffect, useState } from 'react';
import { Settings, KeyRound, Save, Music, Volume2 } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

export default function SettingsTab() {
  const { user, changeAdminCreds } = useAuth();
  const { settings, setSettings, refresh } = useData();
  const { toast } = useToast();
  const [local, setLocal] = useState(settings);
  useEffect(() => { setLocal(settings); }, [settings]);

  const persist = async (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    setSettings(next);
    try {
      await api.put('/api/settings', patch);
      await refresh();
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const persistSocial = async (key, value) => {
    const social = { ...(local.social || {}), [key]: value };
    setLocal({ ...local, social });
    setSettings({ ...local, social });
    try {
      await api.put('/api/settings', { social });
      await refresh();
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  return (
    <>
      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 md:p-8 max-w-2xl">
        <h3 className="text-lg font-bold text-nv-wine mb-2 flex items-center gap-2"><Settings className="w-5 h-5" /> Contact & General Settings</h3>
        <p className="text-sm text-nv-muted mb-6">Update platform-wide contact info shown in footer, checkout & receipts.</p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Business Address</label>
            <input value={local.contactAddress || ''} onChange={(e) => setLocal({ ...local, contactAddress: e.target.value })} onBlur={(e) => persist({ contactAddress: e.target.value })} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Contact Phone</label>
            <input value={local.contactPhone || ''} onChange={(e) => setLocal({ ...local, contactPhone: e.target.value })} onBlur={(e) => persist({ contactPhone: e.target.value })} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">WhatsApp Number (digits with country code)</label>
            <input value={local.whatsapp || ''} onChange={(e) => setLocal({ ...local, whatsapp: e.target.value })} onBlur={(e) => persist({ whatsapp: e.target.value })} placeholder="917250412100" className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Contact Email</label>
            <input type="email" value={local.contactEmail || ''} onChange={(e) => setLocal({ ...local, contactEmail: e.target.value })} onBlur={(e) => persist({ contactEmail: e.target.value })} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Currency</label>
            <select value={local.currency || 'INR'} onChange={(e) => persist({ currency: e.target.value })} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
            </select>
          </div>
        </div>

        <div className="mt-5 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
          ✓ Settings save automatically when you leave a field.
        </div>
      </div>

      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 md:p-8 max-w-2xl mt-6">
        <h3 className="text-lg font-bold text-nv-wine mb-2 flex items-center gap-2"><Music className="w-5 h-5" /> Website Background Music</h3>
        <p className="text-sm text-nv-muted mb-6">A bhakti song plays softly when the website opens. Users can mute/unmute from the floating player.</p>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 border-orange-200 bg-orange-50/40">
            <input
              type="checkbox"
              checked={!!local.musicEnabled}
              onChange={(e) => persist({ musicEnabled: e.target.checked })}
              data-testid="music-enabled-toggle"
              className="w-5 h-5 accent-orange-500"
            />
            <div>
              <div className="font-semibold text-stone-900 text-sm">Enable background music</div>
              <div className="text-xs text-nv-muted">Turn off to hide the player entirely from all visitors.</div>
            </div>
          </label>

          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Song title (shown in player)</label>
            <input
              value={local.musicTitle || ''}
              onChange={(e) => setLocal({ ...local, musicTitle: e.target.value })}
              onBlur={(e) => persist({ musicTitle: e.target.value })}
              placeholder="e.g. Namami Shamishan"
              data-testid="music-title-input"
              className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Direct MP3 URL</label>
            <input
              value={local.musicUrl || ''}
              onChange={(e) => setLocal({ ...local, musicUrl: e.target.value })}
              onBlur={(e) => persist({ musicUrl: e.target.value })}
              placeholder="https://example.com/song.mp3"
              data-testid="music-url-input"
              className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-mono"
            />
            <p className="text-[11px] text-nv-muted mt-1">
              Must be a direct .mp3 (or .m4a) URL — YouTube links won't play. Currently set to a public Nirvana Shatakam recording on archive.org.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-orange-200">
              <input
                type="checkbox"
                checked={local.musicAutoplay !== false}
                onChange={(e) => persist({ musicAutoplay: e.target.checked })}
                data-testid="music-autoplay-toggle"
                className="w-4 h-4 accent-orange-500"
              />
              <span className="text-sm">Autoplay on page open (muted by browser policy)</span>
            </label>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 flex items-center gap-1"><Volume2 className="w-3.5 h-3.5" /> Default volume: {Math.round((local.musicVolume ?? 0.35) * 100)}%</label>
              <input
                type="range" min="0" max="1" step="0.05"
                value={local.musicVolume ?? 0.35}
                onChange={(e) => setLocal({ ...local, musicVolume: +e.target.value })}
                onMouseUp={(e) => persist({ musicVolume: +e.target.value })}
                onTouchEnd={(e) => persist({ musicVolume: +e.target.value })}
                data-testid="music-volume-slider"
                className="w-full accent-orange-500"
              />
            </div>
          </div>

          <div className="pt-3 border-t border-orange-100">
            <div className="text-xs font-semibold text-orange-800 mb-2">Preview</div>
            {local.musicUrl ? (
              <audio controls src={local.musicUrl} className="w-full" />
            ) : (
              <p className="text-xs text-nv-muted italic">Paste a URL above to preview.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 md:p-8 max-w-2xl mt-6">
        <h3 className="text-lg font-bold text-nv-wine mb-2 flex items-center gap-2"><KeyRound className="w-5 h-5" /> Change Admin Login</h3>
        <p className="text-sm text-nv-muted mb-6">Update your admin email &amp; password. You will need the new credentials for the next login.</p>
        <AdminPasswordForm changeAdminCreds={changeAdminCreds} currentEmail={user?.email} toast={toast} />
      </div>

      <div className="bg-white border border-nv-gold/20 rounded-2xl p-6 md:p-8 max-w-2xl mt-6">
        <h3 className="text-lg font-bold text-nv-wine mb-2 flex items-center gap-2">🔗 Social Media Links</h3>
        <p className="text-sm text-nv-muted mb-6">Paste full URLs of your social pages. Leave empty to hide the icon in footer.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ['instagram', 'Instagram', 'https://instagram.com/yourhandle'],
            ['facebook', 'Facebook', 'https://facebook.com/yourpage'],
            ['youtube', 'YouTube', 'https://youtube.com/@yourchannel'],
            ['twitter', 'Twitter / X', 'https://x.com/yourhandle'],
            ['linkedin', 'LinkedIn', 'https://linkedin.com/company/you'],
          ].map(([k, l, ph]) => (
            <div key={k}>
              <label className="text-xs font-medium text-stone-600 mb-1 block">{l}</label>
              <input
                value={(local.social || {})[k] || ''}
                onChange={(e) => setLocal({ ...local, social: { ...(local.social || {}), [k]: e.target.value } })}
                onBlur={(e) => persistSocial(k, e.target.value)}
                placeholder={ph}
                className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function AdminPasswordForm({ changeAdminCreds, currentEmail, toast }) {
  const [email, setEmail] = useState(currentEmail || '');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  useEffect(() => setEmail(currentEmail || ''), [currentEmail]);

  const submit = async (e) => {
    e.preventDefault();
    if (!newPass || newPass.length < 6) return toast({ title: 'Password must be at least 6 characters', variant: 'destructive' });
    if (newPass !== confirmPass) return toast({ title: 'Passwords do not match', variant: 'destructive' });
    const r = await changeAdminCreds(email, newPass);
    if (r.ok) {
      toast({ title: '✓ Admin credentials updated. Use these on next login.' });
      setNewPass(''); setConfirmPass('');
    } else {
      toast({ title: r.error || 'Failed to update credentials', variant: 'destructive' });
    }
  };

  return (
    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="text-xs font-medium text-stone-600 mb-1 block">Admin Email</label>
        <input type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">New Password</label>
        <input type="password" data-testid="admin-newpass" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="At least 6 characters" required className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">Confirm New Password</label>
        <input type="password" data-testid="admin-confirmpass" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} required className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <button data-testid="update-creds-btn" type="submit" className="btn-orange rounded-lg px-6 py-2 font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Update Credentials</button>
      </div>
    </form>
  );
}
